import { Modal, TextField } from 'melp-design/components';
import { useTranslation } from 'react-i18next';
import { ROUTES } from 'config';
import { usePredefinedToasts } from 'utils/Toast';
import { ModalProps } from 'store/modal';
import { FC } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form-latest';
import { routerHistory } from 'utils/router-history';
import { Stack } from '@mui/material';
import { SurveyType, useCopySurvey } from 'store/surveys';
import { capitalizeFirstLetter } from 'utils/general';

interface Values {
  name: string;
}

interface Props extends ModalProps {
  surveyId: string;
  name: string;
  type: SurveyType;
}

export const CopySurveyModal: FC<Props> = ({
  name,
  surveyId,
  type,
  closeModal,
}) => {
  const { t } = useTranslation();
  const predefinedToasts = usePredefinedToasts();

  const { control, handleSubmit } = useForm<Values>({
    defaultValues: {
      name: `(${capitalizeFirstLetter(t('common.copy'))}) ${name}`,
    },
  });

  const { mutate: copySurvey, isLoading: isCopying } = useCopySurvey();

  const onSubmit: SubmitHandler<Values> = async (values) => {
    copySurvey(
      {
        surveyId,
        name: values.name,
      },
      {
        onSuccess: ({ data }) => {
          closeModal();
          predefinedToasts.success.added();
          routerHistory.push(
            `${ROUTES.surveys[type].details.replace(
              ':id',
              data.id,
            )}?guided=true`,
          );
        },
      },
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Modal.Content
        title={t('actions.make_copy')}
        actions={[
          {
            variant: 'neutral-outline',
            onClick: () => closeModal({ action: 'CLOSE' }),
            label: t('common.cancel'),
          },
          {
            variant: 'primary',
            label: t('common.create'),
            type: 'submit',
            disabled: isCopying,
          },
        ]}
      >
        <Stack gap={2}>
          <Controller
            name="name"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <TextField
                label={t('surveys.systemName')}
                name={name}
                value={value}
                onChange={onChange}
                required
                ref={ref}
              />
            )}
          />
        </Stack>
      </Modal.Content>
    </form>
  );
};
